<template>
    <div class="overall-area">
        <div class="print-background report-section">

            <div class="sub-area">
                <div class="section-title">{{ $t('report.ui.overall') }}</div>
                <br>

                <div class="description">
                    <p>
                        作物不同階段有著不同的環境偏好，對逆境的耐受程度也有所不同。
                    </p>
                    <p>
                        我們用逆境警示來提醒農民適時保護作物，也將完整期作的環境資訊整合在這。
                    </p>
                    <p>
                        請切記，農民沒有辦法控制天氣，但他們能在自然的韻律中找到相應的調整方式。
                    </p>
                </div>

                <!-- Sun -->
                <div v-if="data.solar">
                    <b-button variant="success" size="lg" class="tab-buttons active" @click="collapse_sun = !collapse_sun">
                        <span class="block-text">Sun</span>
                        <i :class="$tools.style.classToggle(collapse_sun)"></i>
                    </b-button>
                    <b-collapse id="collapse-overall-sun" class="tab-block" v-model="collapse_sun">

                        <b-row>
                            <b-col md=4>
                                <ChartPie :chartID="'chart-pie-solar'"
                                          :data="arrange_PieData(data.solar, 'solar')"
                                          :unit="$t('u.days')"
                                          :title="$t('report.ui.sun')"
                                          :showLegend="false"
                                          :theme="'dark'"
                                ></ChartPie>
                            </b-col>
                            <b-col md=8>
                                <StatisticSun :data="data.solar" :default_collapse="default_sub_collapse"></StatisticSun>
                            </b-col>
                        </b-row>
                        
                    </b-collapse>
                </div>

                <!-- Air -->
                <div v-if="data.air">
                    <b-button variant="success" size="lg" class="tab-buttons active" @click="collapse_air = !collapse_air">
                        <span class="block-text">Air</span>
                        <i :class="$tools.style.classToggle(collapse_air)"></i>
                    </b-button>
                    <b-collapse id="collapse-overall-air" class="tab-block" v-model="collapse_air">

                        <b-row>
                            <b-col md=4>
                                <ChartPie :chartID="'chart-pie-air'"
                                          :data="arrange_PieData(data.air, 'air')"
                                          :unit="$t('u.days')"
                                          :title="$t('report.ui.air')"
                                          :showLegend="false"
                                          :theme="'dark'"
                                ></ChartPie>
                            </b-col>
                            <b-col md=8>
                                <StatisticAir :data="data.air" :default_collapse="default_sub_collapse"></StatisticAir>
                            </b-col>
                        </b-row>
                        
                    </b-collapse>
                </div>

                <!-- Water -->
                <div v-if="data.water">
                    <b-button variant="success" size="lg" class="tab-buttons active" @click="collapse_water = !collapse_water">
                        <span class="block-text">Water</span>
                        <i :class="$tools.style.classToggle(collapse_water)"></i>
                    </b-button>
                    <b-collapse id="collapse-overall-water" class="tab-block" v-model="collapse_water">

                        <b-row>
                            <b-col md=4>
                                <ChartPie :chartID="'chart-pie-water'"
                                          :data="arrange_PieData(data.water, 'water')"
                                          :unit="$t('u.days')"
                                          :title="$t('report.ui.water')"
                                          :showLegend="false"
                                          :theme="'dark'"
                                ></ChartPie>
                            </b-col>
                            <b-col md=8>
                                <StatisticWater :data="data.water" :default_collapse="default_sub_collapse"></StatisticWater>
                            </b-col>
                        </b-row>
                        
                    </b-collapse>
                </div>

                <!-- Soil -->
                <div v-if="data.soil">
                    <b-button variant="success" size="lg" class="tab-buttons active" @click="collapse_soil = !collapse_soil">
                        <span class="block-text">Soil</span>
                        <i :class="$tools.style.classToggle(collapse_soil)"></i>
                    </b-button>
                    <b-collapse id="collapse-overall-soil" class="tab-block" v-model="collapse_soil">

                        <b-row>
                            <b-col md=4>
                                <ChartPie :chartID="'chart-pie-soil'"
                                          :data="arrange_PieData(data.soil, 'soil')"
                                          :unit="$t('u.days')"
                                          :title="$t('report.ui.soil')"
                                          :showLegend="false"
                                          :theme="'dark'"
                                ></ChartPie>
                            </b-col>
                            <b-col md=8>
                                <StatisticSoil :data="data.soil" :default_collapse="default_sub_collapse"></StatisticSoil>
                            </b-col>
                        </b-row>
                        
                    </b-collapse>
                </div>



            </div>

        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import ChartPie         from '@/views/project/report/Period_1.0/components/Chart_Pie'
import StatisticSun     from '@/views/project/report/Period_1.0/components/Statistic_Sun'
import StatisticAir     from '@/views/project/report/Period_1.0/components/Statistic_Air'
import StatisticWater   from '@/views/project/report/Period_1.0/components/Statistic_Water'
import StatisticSoil    from '@/views/project/report/Period_1.0/components/Statistic_Soil'

export default {
    props: {
        data: {
            type: Object,
            default: function () { return {} },
        },
    },
    components: {
        ChartPie,
        StatisticSun,
        StatisticAir,
        StatisticWater,
        StatisticSoil,
    },
    data () {
        return {
            collapse_sun: true,
            collapse_air: true,
            collapse_water: true,
            collapse_soil: true,
            default_sub_collapse: false,

            colorMap: {
                'solar': {
                    suitable: '#40bf80',
                    danger: '#ff7777',
                    gold: '#d19f42',
                    default: '#99ff33',
                },
                'air': {
                    suitable: '#40bf80',
                    danger: '#ff7777',
                    gold: '#d19f42',
                    default: '#99ff33',
                },
                'water': {
                    suitable: '#40bf80',
                    danger: '#ff7777',
                    gold: '#d19f42',
                    default: '#99ff33',
                },
                'soil': {
                    suitable: '#40bf80',
                    danger: '#ff7777',
                    gold: '#d19f42',
                    default: '#99ff33',
                },
            },
        }
    },
    mounted ()
    {
        this.check_Sub_Collapse()
    },
    computed: {
        ...mapState({
            'screenWidth': state => state.system.screenWidth,
        }),
    },
    watch: {
        // 偵測到 [ 視窗寬度 ] 變動就更新圖表
        screenWidth (val, oldVal)
        {
            if ( val != oldVal )
            {
                this.check_Sub_Collapse()
            }
        },
    },
    methods: {

        check_Sub_Collapse ()
        {
            if ( this.screenWidth > 900 ) this.default_sub_collapse = true
            else this.default_sub_collapse = false
        },

        arrange_PieData (data, type)
        {
            return [
                {
                    name: '氣候適宜',
                    value: data.suitable,
                    color: this.colorMap[ type ].suitable,
                },
                {
                    name: '預警注意',
                    value: data.total - data.suitable,
                    color: this.colorMap[ type ].danger,
                },
            ]
        },

    },
    
}
</script>

<style lang="scss" scoped>

.overall-area
{
    color: white;
    // background-color: rgb(50, 39, 62);
    background-color: rgb(32, 49, 56);
}

.sub-area
{
    padding: 20px 20px;
}

.tab-buttons
{
    min-width: 100px;
    text-align: left;

    i {
        position: relative;
        float: right;
        top: 5px;
    }
}

</style>